import React, { useEffect, useState } from "react";
import "./SalesEventBanner.scss";

function computeTimeRemaining(targetIso) {
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - Date.now();
  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  const pad = (n) => String(n).padStart(2, "0");
  return {
    days: pad(days),
    hours: pad(hours),
    minutes: pad(minutes),
    seconds: pad(seconds),
  };
}

function getContrastTextColor(hexColor) {
  const cleanHex = String(hexColor || "#F59E0B").replace("#", "");
  if (cleanHex.length !== 6) return "#0F172A";
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return "#0F172A";
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#0F172A" : "#FFFFFF";
}

export default function SalesEventBanner({ salesEvent, currency = "", onShopSaleClick }) {
  const [countdown, setCountdown] = useState(null);

  const isScheduled =
    salesEvent?.state === "upcoming" || salesEvent?.status === "scheduled";
  const targetTime = isScheduled ? salesEvent?.starts_at : salesEvent?.ends_at;

  useEffect(() => {
    if (!salesEvent || !targetTime) {
      setCountdown(null);
      return;
    }
    setCountdown(computeTimeRemaining(targetTime));
    const timer = setInterval(() => {
      setCountdown(computeTimeRemaining(targetTime));
    }, 1000);
    return () => clearInterval(timer);
  }, [salesEvent, targetTime]);

  if (
    !salesEvent ||
    salesEvent.shop_banner_enabled === false ||
    salesEvent.show_shop_banner === false
  ) {
    return null;
  }

  const config =
    salesEvent.banner ||
    salesEvent.banner_config ||
    salesEvent.shop_banner_config ||
    {};
  const bgColor = config.bg_color || "#111827";
  const textColor = config.text_color || "#FFFFFF";
  const accentColor = config.accent_color || "#F59E0B";
  const accentTextColor = getContrastTextColor(accentColor);
  const showCountdown = config.show_countdown !== false;
  const bannerImage = config.banner_image_url || "";

  const defaultDiscountLabel =
    salesEvent.discount_summary ||
    (salesEvent.discount_type === "percentage"
      ? `${Math.round(Number(salesEvent.discount_value || 0))}% Off`
      : Number(salesEvent.discount_value || 0) > 0
      ? `${currency}${Number(salesEvent.discount_value || 0).toLocaleString()} Off`
      : "Special Offer");

  const promoEnabled = salesEvent.loyalty_promo_enabled !== false;
  const bonusStampsVal = Number(
    salesEvent.loyalty_bonus_stamps ?? salesEvent.loyalty_bonus_points ?? 0
  );
  const loyaltyLabel = !promoEnabled
    ? null
    : salesEvent.loyalty_promo_type === "multiplier" &&
      Number(salesEvent.loyalty_points_multiplier) > 1
    ? `${Number(salesEvent.loyalty_points_multiplier)}x Points`
    : salesEvent.loyalty_promo_type === "bonus_stamps" && bonusStampsVal > 0
    ? `+${bonusStampsVal} ${bonusStampsVal === 1 ? "Stamp" : "Stamps"}`
    : salesEvent.loyalty_promo_type === "bonus_points" &&
      Number(salesEvent.loyalty_bonus_points) > 0
    ? `+${Number(salesEvent.loyalty_bonus_points)} Points`
    : null;

  const hasCustomBadge = Object.prototype.hasOwnProperty.call(config, "badge_text");
  const badgeText = hasCustomBadge
    ? config.badge_text
    : isScheduled
    ? "Upcoming"
    : defaultDiscountLabel;
  const titleText = config.title || salesEvent.name;
  const subtitleText =
    config.subtitle ||
    [defaultDiscountLabel, loyaltyLabel].filter(Boolean).join(" • ");
  const ctaText = config.cta_text || (isScheduled ? "View Items" : "Shop Sale");

  return (
    <section
      className="lss-sales-banner ls-fade-up"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        backgroundImage: bannerImage
          ? `linear-gradient(90deg, rgba(15, 23, 42, 0.88) 0%, rgba(15, 23, 42, 0.65) 100%), url(${bannerImage})`
          : undefined,
      }}
      aria-label={titleText}
    >
      <div className="lss-sales-banner__inner">
        <div className="lss-sales-banner__content">
          {(badgeText || loyaltyLabel) && (
            <div className="lss-sales-banner__badges">
              {badgeText && (
                <span
                  className="lss-sales-banner__badge"
                  style={{ backgroundColor: accentColor, color: accentTextColor }}
                >
                  {badgeText}
                </span>
              )}
              {loyaltyLabel && (
                <span
                  className="lss-sales-banner__loyalty-pill"
                  style={{ color: textColor }}
                >
                  {loyaltyLabel}
                </span>
              )}
            </div>
          )}

          <h2 className="lss-sales-banner__title" style={{ color: textColor }}>
            {titleText}
          </h2>
          {subtitleText && (
            <p
              className="lss-sales-banner__subtitle"
              style={{ color: textColor }}
            >
              {subtitleText}
            </p>
          )}
        </div>

        <div className="lss-sales-banner__right">
          {showCountdown && countdown && (
            <div className="lss-sales-banner__timer-wrap">
              <span
                className="lss-sales-banner__timer-label"
                style={{ color: textColor }}
              >
                {isScheduled ? "Starts in" : "Ends in"}
              </span>
              <div className="lss-sales-banner__timer" role="timer">
                <div className="lss-sales-banner__unit">
                  <span className="lss-sales-banner__num">{countdown.days}</span>
                  <span className="lss-sales-banner__unit-lbl">d</span>
                </div>
                <span className="lss-sales-banner__sep">:</span>
                <div className="lss-sales-banner__unit">
                  <span className="lss-sales-banner__num">{countdown.hours}</span>
                  <span className="lss-sales-banner__unit-lbl">h</span>
                </div>
                <span className="lss-sales-banner__sep">:</span>
                <div className="lss-sales-banner__unit">
                  <span className="lss-sales-banner__num">{countdown.minutes}</span>
                  <span className="lss-sales-banner__unit-lbl">m</span>
                </div>
                <span className="lss-sales-banner__sep">:</span>
                <div className="lss-sales-banner__unit">
                  <span className="lss-sales-banner__num">{countdown.seconds}</span>
                  <span className="lss-sales-banner__unit-lbl">s</span>
                </div>
              </div>
            </div>
          )}

          {onShopSaleClick && (
            <button
              type="button"
              className="lss-sales-banner__cta"
              style={{ backgroundColor: accentColor, color: accentTextColor }}
              onClick={onShopSaleClick}
            >
              {ctaText}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

